import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { AlertTriangle, AppWindow, KeyRound, Monitor, ShieldCheck, Ticket, UserX } from 'lucide-react';
import { route } from 'ziggy-js';

interface Template {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    icon: string | null;
    category: string | null;
}

interface CatalogPageProps extends PageProps {
    templates: Template[];
    [key: string]: unknown;
}

const iconMap: Record<string, React.ElementType> = {
    Monitor,
    KeyRound,
    AlertTriangle,
    ShieldCheck,
    AppWindow,
    Ticket,
    UserX,
};

const categoryColorMap: Record<string, string> = {
    hardware: 'border-cat-indigo-foreground/30 bg-cat-indigo text-cat-indigo-foreground',
    access: 'border-cat-purple-foreground/30 bg-cat-purple text-cat-purple-foreground',
    software: 'border-cat-teal-foreground/30 bg-cat-teal text-cat-teal-foreground',
};

export default function ServiceRequestCatalogPage() {
    const { props } = usePage<CatalogPageProps>();
    const { templates } = props;
    const { t } = useTranslations();

    const breadcrumbs = [
        { title: t('service_requests.title', 'Service Requests'), href: '/service-requests' },
        { title: t('service_requests.catalog_title', 'Submit a Request'), href: '/service-requests/catalog' },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('service_requests.catalog_title', 'Submit a Request')} />

            <PageHeader
                title={t('service_requests.catalog_title', 'Submit a Request')}
                subtitle={t('service_requests.catalog_subtitle', 'Choose a request type to get started.')}
                actions={
                    <Button variant="outline" asChild>
                        <Link href={route('service-requests.index')}>{t('service_requests.my_requests_title', 'My Requests')}</Link>
                    </Button>
                }
            />

            <PageContent>
                {templates.length === 0 ? (
                    <div className="bg-card rounded-xl border p-12 text-center">
                        <Ticket className="text-muted-foreground mx-auto mb-3 h-8 w-8" />
                        <p className="text-muted-foreground text-sm">{t('service_requests.no_templates', 'No templates available.')}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {templates.map((template) => {
                            const Icon = iconMap[template.icon ?? ''] ?? Ticket;
                            return (
                                <Link
                                    key={template.id}
                                    href={route('service-requests.create', template.id)}
                                    className="bg-card hover:border-primary/50 group flex flex-col gap-3 rounded-xl border p-5 transition-colors"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="bg-muted rounded-lg p-2.5">
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        {template.category && (
                                            <Badge variant="outline" className={categoryColorMap[template.category] ?? ''}>
                                                {template.category}
                                            </Badge>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="font-semibold">{template.name}</h3>
                                        {template.description && (
                                            <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{template.description}</p>
                                        )}
                                    </div>
                                    <span className="text-primary mt-auto text-sm font-medium group-hover:underline">
                                        {t('service_requests.submit_request', 'Submit Request')} →
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </PageContent>
        </AppLayout>
    );
}
